import React, { useEffect, useMemo, useState } from 'react';
import { Alert } from '@mui/material';
import 'jspdf-autotable';
import PartnerMainContainer from '../../../components/PartnerMainContainer';
import TitlePartner from '../component/Title';
import DashboardKpiCard from './DashboardKpiCard';
import DashboardBarListCard from './DashboardBarListCard';
import DashboardDonutCard from './DashboardDonutCard';
import DashboardTimelineFilters from './DashboardTimelineFilters';
import CIList from './CIList';
import PartnerList from './PartnerList';
import PartnerEvalution from '../Parterns/PartnerEvalution';
import partnerDashboardStyles from './partnerdashboard.styles';
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import DashboardService from '../../../services/DashboardService';
// Les KPI sont calculés par le backend, avec les mêmes règles que les listes métier.
// À réactiver avec le contexte financier : parseNumber et formatCompactAmount.

const isSyncedPeriod = (value) => value === true || value === 1 || value === '1';

const getImportedPeriodLabel = (period) => {
  const label = String(period?.part_period_label || '').trim();
  const code = String(period?.part_period_code || '').trim();
  if (label && code && label !== code) return `${label} (${code})`;
  return label || code || 'Période sans libellé';
};

const PartnerDashboard = () => {
  const classes = partnerDashboardStyles();

  const [dashboardKpis, setDashboardKpis] = useState(null);
  /*
   * À réactiver ultérieurement : contexte financier du tableau de bord.
   * const [amountByDirection, setAmountByDirection] = useState([]);
   * const [topPartners, setTopPartners] = useState([]);
   */
  const [financementOptions, setFinancementOptions] = useState([]);
  const [isKpiLoading, setIsKpiLoading] = useState(false);
  const [kpiLoadError, setKpiLoadError] = useState('');
  // À réactiver avec les graphiques financiers : chartLoadError.
  const [financementLoadError, setFinancementLoadError] = useState('');
  const [selectedImportedPeriodId, setSelectedImportedPeriodId] = useState('');
  const [importedPeriods, setImportedPeriods] = useState([]);
  const [contractRange, setContractRange] = useState({ from: '', to: '' });
  const [evaluationRange, setEvaluationRange] = useState({ from: '', to: '' });
  const [selectedFinancement, setSelectedFinancement] = useState('');
  const [ciDialogOpen, setCiDialogOpen] = useState(false);
  const [ciDetailsRows, setCiDetailsRows] = useState([]);
  const [ciDetailsLoading, setCiDetailsLoading] = useState(false);
  const [ciDetailsError, setCiDetailsError] = useState('');
  const [partnerDialogOpen, setPartnerDialogOpen] = useState(false);
  const [partnerDetailsRows, setPartnerDetailsRows] = useState([]);
  const [partnerDetailsLoading, setPartnerDetailsLoading] = useState(false);
  const [partnerDetailsError, setPartnerDetailsError] = useState('');
  const [partnerEvaluationOpen, setPartnerEvaluationOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);

  useEffect(() => {// base importee pour le timeline
    let isMounted = true;

    DashboardService.getPeriods()
      .then((result) => {
        if (!isMounted) return;

        const periods = Array.isArray(result?.data) ? result.data : [];
        setImportedPeriods(periods.filter((period) => isSyncedPeriod(period.is_sync)));
      })
      .catch(() => {
        if (!isMounted) return;
        setImportedPeriods([]);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const kpiQueryParams = useMemo( // filtre 
    () => ({
      part_period_id: selectedImportedPeriodId,
      contract_from: contractRange.from,
      contract_to: contractRange.to,
      evaluation_from: evaluationRange.from,
      evaluation_to: evaluationRange.to,
      financement: selectedFinancement,
    }),
    [selectedImportedPeriodId, contractRange, evaluationRange, selectedFinancement]
  );
  const financementQueryParams = useMemo(
    () => ({
      part_period_id: selectedImportedPeriodId,
      contract_from: contractRange.from,
      contract_to: contractRange.to,
      evaluation_from: evaluationRange.from,
      evaluation_to: evaluationRange.to,
    }),
    [selectedImportedPeriodId, contractRange, evaluationRange]
  );

  useEffect(() => { // get KPIs
    let isMounted = true;

    setIsKpiLoading(true);
    DashboardService.getKpis(kpiQueryParams)
      .then((result) => {
        if (!isMounted) return;

        if(result?.status) {
          setDashboardKpis(result.data || {});
          setKpiLoadError('');
        } else {
          setDashboardKpis(null);
          setKpiLoadError(result?.message || "Impossible de charger les KPI du dashboard partenaire.");
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setDashboardKpis(null);
        setKpiLoadError("Impossible de charger les KPI du dashboard partenaire.");
      })
      .finally(() => {
        if (isMounted) setIsKpiLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [kpiQueryParams]);

  /*
   * À réactiver ultérieurement : appels getAmountByDirection() et
   * getTopPartners(). Les méthodes du service et les endpoints sont conservés.
   *
   * useEffect(() => {
   *   let isMounted = true;
   *   Promise.all([
   *     DashboardService.getAmountByDirection(kpiQueryParams),
   *     DashboardService.getTopPartners(kpiQueryParams),
   *   ]).then(([amountResult, topPartnersResult]) => {
   *     if (!isMounted) return;
   *     setAmountByDirection(amountResult?.status ? amountResult.data : []);
   *     setTopPartners(topPartnersResult?.status ? topPartnersResult.data : []);
   *     setChartLoadError(
   *       amountResult?.status && topPartnersResult?.status
   *         ? ''
   *         : amountResult?.message || topPartnersResult?.message
   *     );
   *   }).catch(() => setChartLoadError('Impossible de charger les graphiques financiers.'));
   *   return () => { isMounted = false; };
   * }, [kpiQueryParams]);
   */

  useEffect(() => {
    let isMounted = true;

    DashboardService.getFinancements(financementQueryParams)
      .then((result) => {
        if (!isMounted) return;

        if (result?.status && Array.isArray(result.data)) {
          setFinancementOptions(result.data);
          setFinancementLoadError('');
        } else {
          setFinancementOptions([]);
          setFinancementLoadError(result?.message || "Impossible de charger les financements.");
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setFinancementOptions([]);
        setFinancementLoadError("Impossible de charger les financements.");
      });

    return () => {
      isMounted = false;
    };
  }, [financementQueryParams]);

  const importedPeriodOptions = useMemo(
    () => importedPeriods
      .map((period) => ({
        value: String(period.part_period_id || ''),
        label: getImportedPeriodLabel(period),
      }))
      .filter((period) => period.value),
    [importedPeriods]
  );

  const handleClearTimeline = () => {
    setSelectedImportedPeriodId('');
    setContractRange({ from: '', to: '' });
    setEvaluationRange({ from: '', to: '' });
    setSelectedFinancement('');
  };

  const timelineInfo = useMemo(() => {
    const filters = [];

    if (contractRange.from || contractRange.to) {
      filters.push(`Contrats: ${contractRange.from || '...'} -> ${contractRange.to || '...'}`);
    }
    if (evaluationRange.from || evaluationRange.to) {
      filters.push(`Evaluations: ${evaluationRange.from || '...'} -> ${evaluationRange.to || '...'}`);
    }
    if (selectedFinancement) {
      filters.push(`Financement: ${selectedFinancement}`);
    }
    if (selectedImportedPeriodId) {
      const period = importedPeriodOptions.find((option) => option.value === selectedImportedPeriodId);
      filters.push(`Base importee: ${period?.label || selectedImportedPeriodId}`);
    }

    return filters.length ? filters.join(' | ') : 'Tous les criteres';
  }, [selectedImportedPeriodId, importedPeriodOptions, contractRange, evaluationRange, selectedFinancement]);

  const handleOpenCIDetails = async () => {
    setCiDialogOpen(true);
    setCiDetailsLoading(true);
    setCiDetailsError('');
    setCiDetailsRows([]);

    try {
      const result = await DashboardService.getCIDetails(kpiQueryParams);

      if (result?.status && Array.isArray(result.data)) {
        setCiDetailsRows(result.data);
        setCiDetailsError('');
      } else {
        setCiDetailsRows([]);
        setCiDetailsError(result?.message || "Impossible de charger le detail des consultants individuels.");
      }
    } catch (error) {
      setCiDetailsRows([]);
      setCiDetailsError("Impossible de charger le detail des consultants individuels.");
    } finally {
      setCiDetailsLoading(false);
    }
  };

  const handleCloseCIDetails = () => {
    setCiDialogOpen(false);
  };

  const handleOpenPartnerDetails = async () => {
    setPartnerDialogOpen(true);
    setPartnerDetailsLoading(true);
    setPartnerDetailsError('');
    setPartnerDetailsRows([]);

    try {
      const result = await DashboardService.getPartnerDetails(kpiQueryParams);

      if (result?.status && Array.isArray(result.data)) {
        setPartnerDetailsRows(result.data);
        setPartnerDetailsError('');
      } else {
        setPartnerDetailsRows([]);
        setPartnerDetailsError(result?.message || "Impossible de charger le detail des partenaires actifs.");
      }
    } catch (error) {
      setPartnerDetailsRows([]);
      setPartnerDetailsError("Impossible de charger le detail des partenaires actifs.");
    } finally {
      setPartnerDetailsLoading(false);
    }
  };

  const handleClosePartnerDetails = () => {
    setPartnerDialogOpen(false);
  };

  const handlePartnerDoubleClick = (partner) => {
    setSelectedPartner({
      partnerId: partner?.partenaire_id || null,
      partnerPid: partner?.pid || partner?.matricule || '',
      partnerName: partner?.partenaire || '',
      sourceDb: partner?.source_db || '',
    });
    setPartnerEvaluationOpen(true);
  };

  const handleClosePartnerEvaluation = () => {
    setPartnerEvaluationOpen(false);
    setSelectedPartner(null);
  };

  const getServerKpiNumber = (key) => {
    const value = dashboardKpis?.[key];
    if (value === null || value === undefined || value === '') return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };
  const formatKpi = (value) => value === null ? '—' : value.toLocaleString('fr-FR');

  const kpiTotalPartners = getServerKpiNumber('totalPartners');
  const kpiActivePartners = getServerKpiNumber('activePartners');
  const kpiTotalMarkets = getServerKpiNumber('totalMarkets');
  const kpiActiveMarkets = getServerKpiNumber('activeMarkets');
  const kpiFinishedMarkets = getServerKpiNumber('finishedMarkets');
  const kpiTerminatedMarkets = getServerKpiNumber('terminatedMarkets');
  // À réactiver ultérieurement : KPI totalMontantActive.
  const kpiMissingEvaluations = getServerKpiNumber('missingEvaluations');
  const kpiGoodEvaluations = getServerKpiNumber('goodEvaluations');
  const kpiAverageEvaluations = getServerKpiNumber('averageEvaluations');
  const kpiPoorEvaluations = getServerKpiNumber('poorEvaluations');
  const kpiAcceptableEvaluations = getServerKpiNumber('acceptableEvaluations');
  const kpiMultiPartners = getServerKpiNumber('multiPartners');
  const kpiCiPartners = getServerKpiNumber('ciPartners');

  const marketSegments = useMemo(
    () => [
      { label: 'EN COURS', value: kpiActiveMarkets, color: '#34d399' },
      { label: 'ACHEVÉ', value: kpiFinishedMarkets, color: '#64748b' },
      { label: 'RESILIÉ', value: kpiTerminatedMarkets, color: '#f87171' },
    ],
    [kpiActiveMarkets, kpiFinishedMarkets, kpiTerminatedMarkets]
  );

  const evaluationsDistribution = useMemo(() => {
    return [
      { label: 'Bonne performance', value: kpiGoodEvaluations, color: '#34d399' },
      { label: 'Performance moyenne', value: kpiAverageEvaluations, color: '#fbbf24' },
      { label: 'Perf. insuffisante', value: kpiPoorEvaluations, color: '#f87171' },
      { label: 'Acceptable', value: kpiAcceptableEvaluations, color: '#4f8ef7' },
      { label: 'Manquant', value: kpiMissingEvaluations, color: '#94a3b8' },
    ];
  }, [
    kpiGoodEvaluations,
    kpiAverageEvaluations,
    kpiPoorEvaluations,
    kpiAcceptableEvaluations,
    kpiMissingEvaluations,
  ]);

  const kpiToneClasses = {
    primary: classes.kpiPrimary,
    success: classes.kpiSuccess,
    danger: classes.kpiDanger,
    warning: classes.kpiWarning,
    purple: classes.kpiPurple,
    teal: classes.kpiTeal,
    orange: classes.kpiOrange,
  };
  const kpiValueClasses = {
    primary: classes.kpiValuePrimary,
    success: classes.kpiValueSuccess,
    danger: classes.kpiValueDanger,
    warning: classes.kpiValueWarning,
    purple: classes.kpiValuePurple,
    teal: classes.kpiValueTeal,
    orange: classes.kpiValueOrange,
  };

  const partnerMarketKpis = [
    {
      label: 'Partenaires actifs',
      value: formatKpi(kpiActivePartners),
      subLabel: `EN COURS - ${formatKpi(kpiTotalPartners)} total`,
      tone: 'primary',
      icon: '🏢',
    },
    {
      label: 'Marchés EN COURS',
      value: formatKpi(kpiActiveMarkets),
      subLabel: `${formatKpi(kpiTotalMarkets)} marches total`,
      tone: 'success',
      icon: "📋",
    },
    /* À réactiver ultérieurement : KPI financier.
    {
      label: 'Montant EN COURS',
      value: formatCompactAmount(kpiTotalMontantActive),
      subLabel: 'MGA',
      tone: 'orange',
      icon: "💰",
    },
    */
    {
      label: 'Marches resilies',
      value: formatKpi(kpiTerminatedMarkets),
      subLabel: `${formatKpi(kpiFinishedMarkets)} contrats achevés`,
      tone: 'danger',
      icon: "🚫",
    },
    {
      label: 'Consultants indiv.',
      value: formatKpi(kpiCiPartners),
      subLabel: 'partenaires CI',
      tone: 'purple',
      icon: "👤",
    },
  ];

  const evaluationKpis = [
    {
      label: 'Sans evaluation',
      value: formatKpi(kpiMissingEvaluations),
      subLabel: 'partenaires EN COURS',
      tone: 'warning',
      icon: "🔔",
    },
    {
      label: 'Bonne performance',
      value: formatKpi(kpiGoodEvaluations),
      subLabel: 'partenaires',
      tone: 'success',
      icon: "⭐",
    },
    {
      label: 'Perf. moyenne',
      value: formatKpi(kpiAverageEvaluations),
      subLabel: 'partenaires',
      tone: 'warning',
      icon: "〰",
    },
    {
      label: 'Perf. insuffisante',
      value: formatKpi(kpiPoorEvaluations),
      subLabel: 'partenaires',
      tone: 'danger',
      icon: "❌",
    },
    {
      label: 'Acceptable',
      value: formatKpi(kpiAcceptableEvaluations),
      subLabel: 'partenaires',
      tone: 'primary',
      icon: "✅",
    },
    {
      label: 'Multi-partenaires',
      value: formatKpi(kpiMultiPartners),
      subLabel: 'conflits detectés',
      tone: 'warning',
      icon: "⚠️",
    },
  ];

  const getPartnerMarketKpiAction = (kpi) => {
    if (kpi.label === 'Partenaires actifs') {
      return {
        onClick: handleOpenPartnerDetails,
        title: 'Afficher les details des partenaires actifs',
        ariaLabel: 'Afficher les details des partenaires actifs',
      };
    }

    if (kpi.label === 'Consultants indiv.') {
      return {
        onClick: handleOpenCIDetails,
        title: 'Afficher les details des consultants individuels',
        ariaLabel: 'Afficher les details des consultants individuels',
      };
    }

    return {};
  };


  return (
    <PartnerMainContainer>
      <LoadingSimpleBackdrop openLoadingSimpleBackdrop={isKpiLoading} />
      <div className={classes.titleRow}>
        <TitlePartner title="Vue d'ensemble" 
        />
        
      </div>

      {kpiLoadError ? (
        <Alert severity="warning" style={{ marginBottom: 12 }}>
          {kpiLoadError}
        </Alert>
      ) : null}
      {/* À réactiver ultérieurement :
        chartLoadError ? <Alert severity="warning">{chartLoadError}</Alert> : null
      */}
      {financementLoadError ? (
        <Alert severity="warning" style={{ marginBottom: 12 }}>
          {financementLoadError}
        </Alert>
      ) : null}

      <DashboardTimelineFilters
        contractRange={contractRange}
        onContractChange={(key, value) => setContractRange((prev) => ({ ...prev, [key]: value }))}
        evaluationRange={evaluationRange}
        onEvaluationChange={(key, value) => setEvaluationRange((prev) => ({ ...prev, [key]: value }))}
        financements={financementOptions}
        selectedFinancement={selectedFinancement}
        onFinancementChange={setSelectedFinancement}
        importedPeriods={importedPeriodOptions}
        selectedImportedPeriodId={selectedImportedPeriodId}
        onImportedPeriodChange={setSelectedImportedPeriodId}
        onClear={handleClearTimeline}
      />

      <div className={classes.alertStack}>
        {kpiMissingEvaluations > 0 ? (
          <div className={`${classes.alert} ${classes.alertWarning}`}>
            🔔 <span className={classes.alertCount}>{kpiMissingEvaluations.toLocaleString('fr-FR')}</span>
            <span>partenaire(s) EN COURS sans evaluation.</span>
          </div>
        ) : null}
        {kpiMultiPartners > 0 ? (
          <div className={`${classes.alert} ${classes.alertDanger}`}>
            ⚠️ <span className={classes.alertCount}>{kpiMultiPartners.toLocaleString('fr-FR')}</span>
            <span>conflit(s) multi-partenaires detecte(s).</span>
          </div>
        ) : null}
      </div>

      <div className={classes.sectionTitle}>🏢 Partenaires et marchés</div>
      <div className={classes.kpiGrid}>
        {partnerMarketKpis.map((kpi) => (
          <DashboardKpiCard
            key={kpi.label}
            classes={classes}
            label={kpi.label}
            value={kpi.value}
            subLabel={kpi.subLabel}
            icon={kpi.icon}
            toneClassName={kpiToneClasses[kpi.tone]}
            valueClassName={kpi.value >= 0 ? kpiValueClasses[kpi.tone] : ''}
            {...getPartnerMarketKpiAction(kpi)}
          />
        ))}
      </div>

      <div className={classes.sectionTitle}>Evaluations</div>
      <div className={classes.kpiGrid}>
        {evaluationKpis.map((kpi) => (
          <DashboardKpiCard
            key={kpi.label}
            classes={classes}
            label={kpi.label}
            value={kpi.value}
            subLabel={kpi.subLabel}
            icon={kpi.icon}
            toneClassName={kpiToneClasses[kpi.tone]}
          />
        ))}
      </div>

      {dashboardKpis && <div className={classes.chartsGrid}>
        <DashboardDonutCard
          title="Situation des marchés"
          segments={marketSegments}
          total={kpiTotalMarkets}
          classes={classes}
        />

        {/* À réactiver ultérieurement :
          <DashboardBarListCard
            title="Montant par direction (MGA)"
            rows={amountByDirection}
            classes={classes}
            valueFormatter={(value) => formatCompactAmount(value)}
            color="#173d61"
          />
          <DashboardBarListCard
            title="Top 7 partenaires - montant total (MGA)"
            rows={topPartners}
            classes={classes}
            valueFormatter={(value) => formatCompactAmount(value)}
            color="#d5ad3f"
          />
        */}

        <DashboardBarListCard
          title="Evaluations - repartition"
          rows={evaluationsDistribution}
          classes={classes}
          valueFormatter={(value) => value.toLocaleString('fr-FR')}
          color="#4f8ef7"
        />
      </div>}

      <CIList
        open={ciDialogOpen}
        onClose={handleCloseCIDetails}
        rows={ciDetailsRows}
        loading={ciDetailsLoading}
        error={ciDetailsError}
        filtersLabel={timelineInfo}
      />

      <PartnerList
        open={partnerDialogOpen}
        onClose={handleClosePartnerDetails}
        rows={partnerDetailsRows}
        loading={partnerDetailsLoading}
        error={partnerDetailsError}
        filtersLabel={timelineInfo}
        onPartnerDoubleClick={handlePartnerDoubleClick}
      />

      <PartnerEvalution
        open={partnerEvaluationOpen}
        onClose={handleClosePartnerEvaluation}
        partnerId={selectedPartner?.partnerId || null}
        partnerPid={selectedPartner?.partnerPid || ''}
        partnerName={selectedPartner?.partnerName || ''}
        sourceDb={selectedPartner?.sourceDb || ''}
      />
     
    </PartnerMainContainer>
  );
};

export default PartnerDashboard;
